import { ROLES } from "~/utils/roles"

/**
 * The route-access DECISION, as a pure function. Given the resolved auth
 * state and a route's meta, it returns WHAT should happen — the global
 * middleware (auth.global.js) does the async work (restore session, sign out
 * a SysAdmin) and then just executes this verdict.
 *
 * Pulled out of the middleware (2026-09-11, auth refactor) so the branching
 * rules — the thing that actually decides who reaches which page — are
 * testable without a Nuxt runtime, and live documented in one place.
 *
 * The RULES, in order of precedence:
 *   1. SysAdmin never uses this app — signed out on sight (the caller does
 *      the sign-out; here we only say "then send to login", unless the
 *      route is unauthenticated-only, where landing is fine once signed out).
 *   2. An unauthenticated-only route (login) with a live session bounces to
 *      the `redirect` query (if a safe in-app path) or the default landing.
 *   3. A protected route with no session goes to login, carrying the path
 *      the user wanted so it can return there.
 *   4. A role-gated route the user's role isn't in → unauthorized.
 *   5. A permission-gated route the user lacks → unauthorized.
 *   6. Otherwise: allow.
 *
 * @param {object} input
 * @param {boolean} input.isAuthenticated
 * @param {string|null} input.role
 * @param {object} input.meta - route meta: { auth, unauthenticatedOnly, roles, permissions }
 * @param {(perm: string) => boolean} input.hasPermission
 * @param {string} input.fullPath - the route being navigated to
 * @param {string} [input.redirectQuery] - `?redirect=` on an unauthenticated-only route
 * @returns {object} one of the verdict shapes documented above
 */
export function decideRouteAccess({
  isAuthenticated,
  role,
  meta = {},
  hasPermission = () => false,
  fullPath = "/",
  redirectQuery,
}) {
  const requiresAuth = meta.auth === true
  const unauthenticatedOnly = meta.unauthenticatedOnly === true
  const allowedRoles = Array.isArray(meta.roles) ? meta.roles : null
  const requiredPermissions = Array.isArray(meta.permissions) ? meta.permissions : null

  const loginRedirect = fullPath && fullPath !== "/" ? fullPath : DEFAULT_LANDING

  // 1. SysAdmin is never a user of this app.
  if (isAuthenticated && role === ROLES.SYSTEM_ADMIN) {
    return unauthenticatedOnly
      ? { type: "signout-allow" }
      : { type: "signout-then-login", redirect: loginRedirect }
  }

  // 2. Already signed in, on a login-type page → send them into the app.
  if (unauthenticatedOnly && isAuthenticated) {
    const safe =
      typeof redirectQuery === "string" && redirectQuery.startsWith("/")
        ? redirectQuery
        : DEFAULT_LANDING

    
    return { type: "redirect", to: safe }
  }

  // 3. Protected route, no session.
  if (requiresAuth && !isAuthenticated) {
    return { type: "login", redirect: loginRedirect }
  }

  // 4. Role gate.
  if (allowedRoles && allowedRoles.length > 0) {
    if (!isAuthenticated) return { type: "login", redirect: loginRedirect }
    if (!role || !allowedRoles.includes(role)) {
      return { type: "unauthorized", from: fullPath, role: allowedRoles.join(",") }
    }
  }

  // 5. Permission gate.
  if (requiredPermissions && requiredPermissions.length > 0) {
    if (!isAuthenticated) return { type: "login", redirect: loginRedirect }
    const missing = requiredPermissions.filter(p => !hasPermission(p))
    if (missing.length > 0) {
      return { type: "unauthorized", from: fullPath, permission: missing.join(",") }
    }
  }

  // 6. Nothing blocks it.
  return { type: "allow" }
}

/** Where a login (or a stray bounce) lands when no specific path is asked for. */
export const DEFAULT_LANDING = "/follow-ups"
